import { z } from "zod";
/**
 * Course Progress Schema for DynamoDB
 *
 * Progress is stored with denormalized course info for efficient queries.
 *
 * PK: USER#<uid>
 * SK: PROGRESS#COURSE#<courseId>
 */
// Base CourseProgress schema for DynamoDB
// Matches server ProgressRepository.itemToCourseProgress() output
export const CourseProgressSchema = z.object({
    user_uid: z.string(),
    course_id: z.number().int(),
    status: z.enum(["enrolled", "in_progress", "completed"]),
    modules_completed: z.number().int().min(0).default(0),
    modules_total: z.number().int().min(0).default(0),
    exercises_completed: z.number().int().min(0).default(0),
    exercises_total: z.number().int().min(0).default(0),
    completion_percentage: z.number().min(0).max(100).default(0),
    time_spent_seconds: z.number().int().min(0).default(0),
    enrolled_at: z.date(),
    started_at: z.date().nullable(),
    completed_at: z.date().nullable(),
    updated_at: z.date().optional(),
    // Denormalized course info
    course_name: z.string().optional(),
    course_slug: z.string().optional(),
});
// Payload schema for creating/updating course progress
export const CourseProgressPayloadSchema = z.object({
    course_id: z.number().int(),
    user_uid: z.string(),
    modules_total: z.number().int().min(0).optional(),
    exercises_total: z.number().int().min(0).optional(),
    course_name: z.string().optional(),
    course_slug: z.string().optional(),
});
// Schema for user course progress with course details (alias)
export const UserCourseProgressSchema = CourseProgressSchema;
// Schema for course progress statistics
export const CourseProgressStatsSchema = z.object({
    total_users_enrolled: z.number().int().min(0),
    total_users_completed: z.number().int().min(0),
    average_progress_percentage: z.number().min(0).max(100),
    average_exercises_completed: z.number().min(0),
    average_time_spent: z.number().min(0),
    completion_rate: z.number().min(0).max(100),
});
// Schema for course leaderboard entries
export const CourseLeaderboardEntrySchema = z.object({
    user_uid: z.string(),
    user_name: z.string().nullable(),
    completion_percentage: z.number().min(0).max(100),
    exercises_completed: z.number().int().min(0),
    time_spent_seconds: z.number().int().min(0),
    completed_at: z.date().nullable(),
});
// Schema for recently active users
export const RecentlyActiveUserSchema = z.object({
    user_uid: z.string(),
    user_name: z.string().nullable(),
    completion_percentage: z.number().min(0).max(100),
});
// Schema for filters used in queries
export const CourseProgressFiltersSchema = z
    .record(z.string(), z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.date(),
    z.array(z.union([z.string(), z.number(), z.boolean()])),
]))
    .optional();
// Schema for course progress update
export const CourseProgressUpdateSchema = z.object({
    userUid: z.string(),
    courseId: z.number().int(),
});
// Schema for recently active users query parameters
export const RecentlyActiveUsersQuerySchema = z.object({
    courseId: z.number().int(),
    days: z.number().int().min(1).max(365).default(7),
    limit: z.number().int().min(1).max(100).default(20),
});
